import React from "react";
import { Box, Button, Typography, Container } from "@mui/material";
import { motion } from "framer-motion";

import ChicaCta from "../static/media/ChicaDemo.webp";

const MotionBox = motion(Box);
const MotionButton = motion(Button);

const benefits = [
  "Implementación incluida",
  "Soporte técnico continuo",
  "Capacitación guiada",
];

const CtaBanner = () => {
  return (
    <Box
      component="section"
      id="cta-section"
      sx={{
        py: { xs: 8, md: 12 },
        background: "linear-gradient(180deg,#ffffff 0%,#f7faff 100%)",
        overflow: "hidden",
      }}
    >
      <Container maxWidth="xl">
        <MotionBox
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.7 }}
          sx={{
            position: "relative",
            overflow: "hidden",
            borderRadius: "36px",
            border: "1px solid rgba(14,165,233,.10)",
            background: `
              linear-gradient(
                180deg,
                #ffffff 0%,
                #f8fbff 55%,
                #eef7ff 100%
              )
            `,
            boxShadow: "0 30px 80px rgba(13,93,181,.12)",
            px: { xs: 3, md: 7 },
            pt: { xs: 5, md: 7 },
            pb: { xs: 5, md: 7 }, // Añadimos padding abajo para balancear
          }}
        >
          {/* Banda conectada con Hero */}
          <Box
            sx={{
              position: "absolute",
              top: 0,
              left: 0,
              right: 0,
              height: "8px",
              background:
                "linear-gradient(90deg,#1A2B4A 0%,#0D5DB5 45%,#0EA5E9 100%)",
            }}
          />

          {/* Efectos Glow */}
          <Box
            sx={{
              position: "absolute",
              right: { xs: "-200px", md: "50px" },
              bottom: -120,
              width: 520,
              height: 520,
              borderRadius: "50%",
              background:
                "radial-gradient(circle, rgba(14,165,233,.30) 0%, rgba(14,165,233,.12) 40%, rgba(14,165,233,0) 75%)",
              pointerEvents: "none",
            }}
          />
          <Box
            sx={{
              position: "absolute",
              right: 150,
              top: 50,
              width: 250,
              height: 250,
              borderRadius: "50%",
              background:
                "radial-gradient(circle, rgba(13,93,181,.10) 0%, transparent 70%)",
            }}
          />

          <Box
            sx={{
              display: "flex",
              flexDirection: { xs: "column", lg: "row" },
              alignItems: "center",
              justifyContent: "space-between",
              gap: { xs: 6, sm: 8, lg: 6 },
              position: "relative",
              zIndex: 2,
            }}
          >
            {/* ─── COLUMNA IZQUIERDA: TEXTO ─── */}
            <Box sx={{ flex: 1, maxWidth: { lg: "650px" } }}>
              <Typography
                component="h2"
                sx={{
                  fontWeight: 800,
                  color: "#1A2B4A",
                  lineHeight: 1.05,
                  letterSpacing: "-.04em",
                  fontSize: { xs: "2.2rem", md: "4rem" },
                }}
              >
                Tu operación, lista
                <br />
                para el{" "}
                <Box
                  component="span"
                  sx={{
                    background: "linear-gradient(135deg,#0D5DB5,#0EA5E9)",
                    WebkitBackgroundClip: "text",
                    WebkitTextFillColor: "transparent",
                  }}
                >
                  siguiente nivel
                </Box>
              </Typography>

              <Typography
                sx={{
                  mt: 3,
                  color: "#64748B",
                  fontSize: { xs: "1rem", md: "1.15rem" },
                  lineHeight: 1.8,
                  maxWidth: 600,
                }}
              >
                Nuestro equipo te acompaña desde el primer día: configuración,
                capacitación y soporte incluidos. Empieza a facturar y controlar
                tu negocio sin complicaciones ni costos ocultos.
              </Typography>

              {/* Beneficios */}
              <Box
                sx={{
                  mt: 4,
                  display: "grid",
                  gridTemplateColumns: { xs: "1fr", md: "repeat(3,1fr)" },
                  gap: 2,
                  maxWidth: 760,
                }}
              >
                {benefits.map((item) => (
                  <Box
                    key={item}
                    sx={{
                      p: 2,
                      borderRadius: "18px",
                      background: "#fff",
                      border: "1px solid rgba(13,93,181,.08)",
                      boxShadow: "0 8px 20px rgba(13,93,181,.05)",
                      display: "flex",
                      gap: 1.5,
                      alignItems: "center",
                    }}
                  >
                    <Box
                      sx={{
                        width: 42,
                        height: 42,
                        borderRadius: "12px",
                        background: "linear-gradient(135deg,#0D5DB5,#2563EB)",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        color: "#fff",
                        fontWeight: 800,
                        flexShrink: 0,
                      }}
                    >
                      ✓
                    </Box>
                    <Typography
                      sx={{
                        color: "#1A2B4A",
                        fontWeight: 700,
                        fontSize: ".95rem",
                      }}
                    >
                      {item}
                    </Typography>
                  </Box>
                ))}
              </Box>
            </Box>

            {/* ─── COLUMNA DERECHA: IMAGEN + BOTÓN CTA ─── */}
            <MotionBox
              initial={{ opacity: 0, x: 40 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
              sx={{
                position: "relative",
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "flex-end", // Asegura que el botón quede anclado abajo
                width: { xs: "100%", lg: "480px" },
                height: { xs: "320px", sm: "400px", md: "500px" }, // Altura ajustada para el efecto
                flexShrink: 0,
              }}
            >
              {/* Glow detrás de la chica */}
              <Box
                sx={{
                  position: "absolute",
                  width: { xs: 250, md: 400 }, // Lo hacemos un poco más grande para que cubra bien
                  height: { xs: 250, md: 400 },
                  borderRadius: "80%",
                  top: "10%",

                  // 1. MOVER A LA DERECHA: Empujamos el filtro azul hacia la derecha
                  transform: { xs: "translateX(40px)", md: "translateX(80px)" },

                  // 2. SUAVIZAR BORDES: Ajustamos el difuminado para que no deje líneas duras
                  background:
                    "radial-gradient(circle, rgba(14,165,233,.15) 0%, rgba(14,165,233,.06) 45%, rgba(14,165,233,0) 70%)",
                  zIndex: 0,
                }}
              />

              {/* IMAGEN DE LA CHICA (Z-Index 1 - Detrás del botón) */}
              <Box
                component="img"
                src={ChicaCta}
                alt="Demo gratuita"
                sx={{
                  position: "absolute",
                  bottom: { xs: 40, md: 60 }, // Se esconde justo detrás del botón
                  transform: {
                    xs: "translateX(-10px)",
                    md: "translateX(-25px)",
                  },
                  zIndex: 1,
                  height: { xs: "85%", md: "90%" }, // Sobresale hacia arriba
                  objectFit: "contain",
                  filter: "drop-shadow(0 20px 40px rgba(13,93,181,.15))",

                  // Mantenemos un leve difuminado por si la imagen es más ancha que el botón
                  WebkitMaskImage:
                    "linear-gradient(to bottom, black 85%, transparent 100%)",
                  maskImage:
                    "linear-gradient(to bottom, black 85%, transparent 100%)",
                }}
              />

              {/* BOTÓN (Z-Index 2 - Cubre el corte de la chica) */}
              <MotionButton
                component="a"
                href="https://api.whatsapp.com/send/?phone=593984493776&text=Hola,%20quisiera%20agendar%20una%20demostraci%C3%B3n%20de%20FactuSysDR"
                target="_blank"
                rel="noopener noreferrer"
                whileHover={{ y: -4, scale: 1.02 }}
                whileTap={{ scale: 0.97 }}
                variant="contained"
                sx={{
                  position: "relative",
                  zIndex: 2,
                  width: { xs: "90%", sm: "340px", md: "100%" },
                  py: { xs: 2, md: 2.5 },
                  borderRadius: "16px",
                  textTransform: "none",
                  fontWeight: 800,
                  fontSize: { xs: "1.05rem", md: "1.2rem" },
                  letterSpacing: "0.01em",
                  color: "#fff",
                  background: "linear-gradient(135deg,#2C5FBE 0%,#1A3D8F 100%)",
                  boxShadow: "0 15px 35px rgba(26,61,143,0.4)",
                  border: "2px solid rgba(255,255,255,0.2)",

                  // NUEVO
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                  textAlign: "center",

                  "& .MuiButton-label": {
                    width: "100%",
                    textAlign: "center",
                  },

                  "&:hover": {
                    background:
                      "linear-gradient(135deg,#214A96 0%,#162E6B 100%)",
                    boxShadow: "0 20px 45px rgba(26,61,143,0.5)",
                  },
                }}
              >
                Agenda una demostración
              </MotionButton>
            </MotionBox>
          </Box>
        </MotionBox>
      </Container>
    </Box>
  );
};

export default CtaBanner;
